import { isReferenceIdIncludedInContextByFilter } from '@ardoq/reference-interface';
import { isComponentIdIncludedInContextByFilter } from '@ardoq/component-interface';
import { GlobalReferenceTypeId } from '@ardoq/data-model';
import { ArdoqId, LinkedComponent, ReferenceDirection } from '@ardoq/api-types';
import {
  CapabilityMapModelData,
  ListValuesMap,
  RefTypeIds,
  TemporaryDict,
  ViewSettingsShape,
} from './types';
import { getComponentLabelParts, truncateComponentLabel } from '@ardoq/graph';
import { type Point, geometry } from '@ardoq/math';
import { returnZero } from '@ardoq/common-helpers';
import {
  getReferenceLabelFormattingFieldLabel,
  getReferenceLabelFormattingFieldValue,
} from 'modelInterface/util';
import { safePositiveNumeric } from '@ardoq/dependency-map';
import { COMPONENT_ID_SELECTOR } from 'consts';

type PrioritySorter = (a: any, b: any) => number;
type LinkedComponentsPerRefType = Map<GlobalReferenceTypeId, Set<ArdoqId>>;

const isUnset = (value: any) => value === null || value === undefined;

const CLICK_THRESHOLD = 15;

const TARGET_RATIO = 4 / 3;
// Ratio of a standard leaf box in pixels dimensions
const RATIO_STANDARD_BOX = 4 / 1;
// A estmate based on the empty cells
const AVERAGE_COMPACTNESS = 0.9;

export const getIdealGridWidth = (leavesCount: number) => {
  const adjustedLeavesCount = leavesCount / AVERAGE_COMPACTNESS;
  return Math.round(
    (adjustedLeavesCount * (TARGET_RATIO / RATIO_STANDARD_BOX)) ** 0.5
  );
};

export const isClick = (event: React.MouseEvent, mouseDownPosition: Point) =>
  geometry.distanceTwoArgs([event.clientX, event.clientY], mouseDownPosition) <
  CLICK_THRESHOLD;

export const ROOT_NODE_ID = '@@root-node@@';

export const getRootNode = (
  modelData: CapabilityMapModelData,
  priorityKey: string,
  groupingKey: string,
  prioritySorter: PrioritySorter,
  listValuesMap: ListValuesMap,
  supportingValue: string,
  childrenIds: ArdoqId[]
): TemporaryDict => {
  const fallbackPriorityValue = getFallbackPriorityValue(
    listValuesMap,
    supportingValue
  );
  const children = childrenIds
    .filter(childId => isComponentIdIncludedInContextByFilter(childId))
    .map(
      toViewModel(
        modelData,
        priorityKey,
        groupingKey,
        prioritySorter,
        listValuesMap,
        supportingValue,
        fallbackPriorityValue,
        ReferenceDirection.NONE
      )
    )
    .sort(prioritySorter);

  const { supportingColumns, priorityRows, anyRow } = getGroups(
    children,
    supportingValue,
    fallbackPriorityValue
  );

  return {
    id: ROOT_NODE_ID,
    name: '',
    classNames: '',
    representationData: { isImage: false, value: null },
    priorityValue: undefined,
    isGrouping: false,
    priority: undefined,
    navigatorOrder: 0,
    layoutType: '',
    leavesCount: 0,
    minGridWidth: 1,
    children,
    collapsedDescendants: [],
    isCollapsed: false,
    supportingColumns,
    priorityRows,
    anyRow,
    referencedDirection: ReferenceDirection.NONE,
    referenceLabel: '',
    referenceFieldValue: '',
    referenceFieldLabel: '',
    isCollapsedReference: false,
  };
};

const toViewModel =
  (
    modelData: CapabilityMapModelData,
    priorityKey: string,
    groupingKey: string,
    prioritySorter: PrioritySorter,
    listValuesMap: ListValuesMap,
    supportingValue: string,
    fallbackPriorityValue: FallbackPriorityValue,
    referencedDirection: ReferenceDirection = ReferenceDirection.NONE,
    indexOffset = 0,
    referencedLabels: (string | null)[] = [],
    referencedFieldValues: (string | null)[] = [],
    isCollapsedReference = false
  ) =>
  (componentId: ArdoqId, index = 0): TemporaryDict => {
    const componentData = modelData.componentData.get(componentId);
    const attributes = componentData?.attributes;
    const isReferenced = referencedDirection !== ReferenceDirection.NONE;
    const priorityValue = isReferenced
      ? undefined
      : (attributes?.[priorityKey] as string | undefined);
    const children = isReferenced
      ? []
      : (componentData?.childrenIds ?? [])
          .map(
            toViewModel(
              modelData,
              priorityKey,
              groupingKey,
              prioritySorter,
              listValuesMap,
              supportingValue,
              fallbackPriorityValue,
              referencedDirection
            )
          )
          .sort(prioritySorter);

    const { supportingColumns, priorityRows, anyRow } = getGroups(
      children,
      supportingValue,
      fallbackPriorityValue
    );
    const id = attributes?._id ?? componentId;
    const name = truncateComponentLabel({
      ...getComponentLabelParts(componentId),
      measure: returnZero,
      width: Infinity,
    });
    const classNames = componentData?.cssClassNames ?? '';
    const iconColor = componentData?.iconColor ?? '';
    return {
      id,
      name,
      classNames,
      iconColor,
      representationData: componentData?.representationData ?? {
        isImage: false,
        value: null,
      },
      priorityValue,
      isGrouping: attributes?.typeId === groupingKey,
      priority:
        priorityValue !== null && priorityValue !== undefined
          ? listValuesMap[priorityValue]
          : undefined,
      navigatorOrder: indexOffset + index,
      layoutType: '',
      leavesCount: 0,
      minGridWidth: 1,
      children,
      collapsedDescendants: [],
      isCollapsed: false,
      supportingColumns,
      priorityRows,
      anyRow,
      referencedDirection,
      referenceLabel: referencedLabels[index] || '',
      referenceFieldValue: referencedFieldValues[index] || '',
      referenceFieldLabel: getReferenceLabelFormattingFieldLabel() || '',
      isCollapsedReference,
    };
  };

type AddBothDirectionsReferencedComponentsArgs = {
  modelData: CapabilityMapModelData;
  node: TemporaryDict;
  targetAndSourceNodeId: string;
  includeIncomingReferenceTypes: GlobalReferenceTypeId[];
  includeOutgoingReferenceTypes: GlobalReferenceTypeId[];
  incomingLinkedComponentsPerRefType: LinkedComponentsPerRefType;
  outgoingLinkedComponentsPerRefType: LinkedComponentsPerRefType;
  priorityKey: string;
  groupingKey: string;
  prioritySorter: PrioritySorter;
  listValuesMap: ListValuesMap;
  supportingValue: string;
  fallbackPriorityValue: FallbackPriorityValue;
  collapsed: boolean;
};
const addBothDirectionsOfReferencedComponents = ({
  modelData,
  node,
  targetAndSourceNodeId,
  includeIncomingReferenceTypes,
  includeOutgoingReferenceTypes,
  incomingLinkedComponentsPerRefType,
  outgoingLinkedComponentsPerRefType,
  priorityKey,
  groupingKey,
  prioritySorter,
  listValuesMap,
  supportingValue,
  fallbackPriorityValue,
  collapsed,
}: AddBothDirectionsReferencedComponentsArgs) => {
  addReferencedComponents(
    modelData,
    node,
    modelData.sourceMap.get(targetAndSourceNodeId) ?? [],
    includeOutgoingReferenceTypes,
    ReferenceDirection.OUTGOING,
    outgoingLinkedComponentsPerRefType,
    priorityKey,
    groupingKey,
    prioritySorter,
    listValuesMap,
    supportingValue,
    fallbackPriorityValue,
    collapsed
  );
  addReferencedComponents(
    modelData,
    node,
    modelData.targetMap.get(targetAndSourceNodeId) ?? [],
    includeIncomingReferenceTypes,
    ReferenceDirection.INCOMING,
    incomingLinkedComponentsPerRefType,
    priorityKey,
    groupingKey,
    prioritySorter,
    listValuesMap,
    supportingValue,
    fallbackPriorityValue,
    collapsed
  );
};

export const addAllReferencedComponents = (
  modelData: CapabilityMapModelData,
  node: TemporaryDict,
  includeIncomingReferenceTypes: GlobalReferenceTypeId[],
  includeOutgoingReferenceTypes: GlobalReferenceTypeId[],
  priorityKey: string,
  groupingKey: string,
  prioritySorter: PrioritySorter,
  listValuesMap: ListValuesMap,
  supportingValue: string,
  fallbackPriorityValue: FallbackPriorityValue = getFallbackPriorityValue(
    listValuesMap,
    supportingValue
  )
) => {
  const { isCollapsed, children, collapsedDescendants, id: nodeId } = node;
  if (!isCollapsed) {
    children.forEach(child =>
      addAllReferencedComponents(
        modelData,
        child,
        includeIncomingReferenceTypes,
        includeOutgoingReferenceTypes,
        priorityKey,
        groupingKey,
        prioritySorter,
        listValuesMap,
        supportingValue,
        fallbackPriorityValue
      )
    );
  }

  const collapsedDescendantNodeIds = collapsedDescendants.map(({ id }) => id);

  const incomingLinkedComponentsPerRefType = new Map();
  const outgoingLinkedComponentsPerRefType = new Map();

  collapsedDescendantNodeIds.forEach(collapsedDescendantNodeId =>
    addBothDirectionsOfReferencedComponents({
      modelData,
      node,
      targetAndSourceNodeId: collapsedDescendantNodeId,
      includeIncomingReferenceTypes,
      includeOutgoingReferenceTypes,
      incomingLinkedComponentsPerRefType,
      outgoingLinkedComponentsPerRefType,
      priorityKey,
      groupingKey,
      prioritySorter,
      listValuesMap,
      supportingValue,
      fallbackPriorityValue,
      collapsed: true,
    })
  );

  addBothDirectionsOfReferencedComponents({
    modelData,
    node,
    targetAndSourceNodeId: nodeId,
    includeIncomingReferenceTypes,
    includeOutgoingReferenceTypes,
    incomingLinkedComponentsPerRefType,
    outgoingLinkedComponentsPerRefType,
    priorityKey,
    groupingKey,
    prioritySorter,
    listValuesMap,
    supportingValue,
    fallbackPriorityValue,
    collapsed: false,
  });
};

const isAlreadyLinkedComponent = (
  linkedComponentsPerRefType: LinkedComponentsPerRefType,
  componentId: ArdoqId,
  refTypeId: GlobalReferenceTypeId | null
): boolean => {
  if (refTypeId) {
    let linkedCompIds = linkedComponentsPerRefType.get(refTypeId);
    if (!linkedCompIds) {
      linkedCompIds = new Set();
      linkedComponentsPerRefType.set(refTypeId, linkedCompIds);
    }
    if (linkedCompIds.has(componentId)) {
      return true;
    }
    linkedCompIds.add(componentId);
  }
  return false;
};

const filterLinkedComponents =
  (
    modelData: CapabilityMapModelData,
    linkedComponentsPerRefType: LinkedComponentsPerRefType,
    includeReferenceTypes: GlobalReferenceTypeId[]
  ) =>
  ({ referenceId, componentId }: LinkedComponent) => {
    if (
      !(
        isComponentIdIncludedInContextByFilter(componentId) &&
        isReferenceIdIncludedInContextByFilter(referenceId)
      )
    ) {
      return false;
    }

    const globalRefTypeId =
      modelData.referenceData.get(referenceId)?.globalTypeId ?? null;
    if (
      isAlreadyLinkedComponent(
        linkedComponentsPerRefType,
        componentId,
        globalRefTypeId
      )
    ) {
      return false;
    }

    const showAll = includeReferenceTypes.includes(RefTypeIds.ALL);
    return (
      showAll ||
      !globalRefTypeId ||
      includeReferenceTypes.includes(globalRefTypeId)
    );
  };

const addReferencedComponents = (
  modelData: CapabilityMapModelData,
  node: TemporaryDict,
  allLinkedComponents: LinkedComponent[],
  includeReferenceTypes: GlobalReferenceTypeId[],
  referencedDirection: ReferenceDirection,
  linkedComponentsPerRefType: LinkedComponentsPerRefType,
  priorityKey: string,
  groupingKey: string,
  prioritySorter: PrioritySorter,
  listValuesMap: ListValuesMap,
  supportingValue: string,
  fallbackPriorityValue: FallbackPriorityValue,
  collapsed: boolean
) => {
  const linkedComponents = allLinkedComponents.filter(
    filterLinkedComponents(
      modelData,
      linkedComponentsPerRefType,
      includeReferenceTypes
    )
  );

  const referencedComponents = linkedComponents.map(
    ({ componentId }) => componentId
  );
  const incomingReferenceLabels = linkedComponents.map(
    ({ referenceId }) =>
      modelData.referenceData.get(referenceId)?.displayLabel ?? null
  );
  const incomingReferenceFieldValues = linkedComponents.map(({ referenceId }) =>
    getReferenceLabelFormattingFieldValue(referenceId)
  );

  const indexOffset = node.children.length;

  const referencedViewModels = referencedComponents.map(
    toViewModel(
      modelData,
      priorityKey,
      groupingKey,
      prioritySorter,
      listValuesMap,
      supportingValue,
      fallbackPriorityValue,
      referencedDirection,
      indexOffset,
      incomingReferenceLabels,
      incomingReferenceFieldValues,
      collapsed
    )
  );

  node.children.push(...referencedViewModels);
  node.anyRow.push(...referencedViewModels);
};

const getGroups = (
  children: TemporaryDict[],
  supportingValue: string,
  fallbackPriorityValue: FallbackPriorityValue
) => {
  const supportingColumns = children.filter(
    child => child.priorityValue === supportingValue
  );
  let anyRow = children.filter(child => isUnset(child.priorityValue));
  const priorityRows = children
    .filter(
      child =>
        !(
          child.priorityValue === supportingValue ||
          isUnset(child.priorityValue)
        )
    )
    .reduce<TemporaryDict[][]>((acc, child) => {
      const lastRow = acc[acc.length - 1];
      if (!lastRow || lastRow[0].priorityValue !== child.priorityValue) {
        return [...acc, [child]];
      }
      lastRow.push(child);
      return acc;
    }, []);

  if (priorityRows.length === 0 && anyRow.length > 0) {
    const anyRowWithPriorityRows = anyRow.find(
      child => child.priorityRows.length > 0
    );
    if (anyRowWithPriorityRows) {
      priorityRows.push([
        Object.assign(anyRowWithPriorityRows, fallbackPriorityValue),
      ]);
      anyRow = anyRow.filter(child => child !== anyRowWithPriorityRows);
    }
  }

  return { supportingColumns, priorityRows, anyRow };
};

type FallbackPriorityValue = { priorityValue: string; priority: number };

const getFallbackPriorityValue = (
  listValuesMap: ListValuesMap,
  supportingValue: string
): FallbackPriorityValue => {
  const [priorityValue, priority] = Object.entries(listValuesMap).find(
    ([key]) => key !== supportingValue
  ) || ['unknown', 0];
  return { priorityValue, priority };
};

/**
 * A treeDepth of null, or an invalid treeDepth, is coerced to Infinity (fully expanded).
 * Because Infinity is not serializable as JSON, a saved slide is likely to have a null treeDepth.
 */
export const resolveCapabilityMapViewSettings = (
  viewSettings: ViewSettingsShape
): ViewSettingsShape => ({
  ...viewSettings,
  treeDepth: safePositiveNumeric(viewSettings.treeDepth),
});

export const getComponentIdFromTarget = (event: React.MouseEvent) => {
  const target = (event.target as Element).closest<HTMLElement>(
    COMPONENT_ID_SELECTOR
  );
  return target ? target.dataset.componentId : '';
};
